<template>
  <div class="community-curation">
    <div class="tw-container px-4 py-6">

      <!-- Header -->
      <div class="mb-6 tw-flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">{{ $t('curation') }}</h2>
          <p class="text-sm text-gray-500 mt-1">{{ $t('community_curation_subtitle') }}</p>
        </div>
        <div v-if="activeTab === tabNames.knowledgeBase || activeTab === tabNames.newActors || activeTab === tabNames.claims || activeTab === tabNames.deletedActors"
             class="w-full sm:w-56">
          <DsInput
            v-model="searchQuery"
            icon="search"
            is-simplified
            :placeholder="$t('community_curation_search_placeholder')"
          />
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="mb-6 bg-white rounded-lg shadow-sm border p-1 tw-flex gap-1 overflow-x-auto">
        <button
          v-for="tab in tabs"
          :key="tab"
          @click="switchTab(tab)"
          v-tooltip.top="!tabIsEnabled(tab) ? getDisabledTabTooltip(tab) : ''"
          :disabled="!tabIsEnabled(tab)"
          class="tab-pill tw-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap"
          :class="[!tabIsEnabled(tab) ? '!bg-neutral-300 text-white hover:text-white cursor-none' : '', activeTab === tab ? 'tab-pill--active' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800']"
        >
          {{ $t('community_curation_tab_' + tab) }}
          <span
            v-if="tabIsEnabled(tab)"
            class="badge-count text-xs font-semibold px-1.5 py-0.5 rounded-full"
            :class="activeTab === tab ? 'badge-count--active' : 'bg-gray-100 text-gray-600'"
          >{{ getTabCount(tab) ?? '0' }}</span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="tw-flex items-center justify-center py-16">
        <loading/>
      </div>

      <template v-else>

        <!-- JOIN REQUESTS -->
        <div v-if="activeTab === tabNames.communityJoinRequests">
          <div v-if="communityJoinRequests.length === 0" class="text-center py-16 px-8 bg-white rounded-lg border border-gray-200">
            <div class="bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4 text-gray-400" style="width:56px;height:56px;flex-shrink:0">
              <Icon name="users" size="24"/>
            </div>
            <p class="text-base font-semibold text-gray-700 mb-1">{{ $t('community_curation_join_requests_empty_title') }}</p>
            <p class="text-sm text-gray-400">{{ $t('community_curation_join_requests_empty_sub') }}</p>
          </div>
          <div v-else class="tw-flex flex-col gap-3">
            <div
              v-for="req in communityJoinRequests"
              :key="req.id"
              class="tw-flex flex-wrap items-center gap-4 bg-white rounded-lg border border-gray-200 px-5 py-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div
                class="w-10 h-10 rounded-full text-white font-semibold text-sm tw-flex items-center justify-center shrink-0 cursor-pointer"
                style="background-color: var(--primary-community, #3b82f6)"
                @click="openUserPanel(req.user.id)"
              >
                {{ initials(req.user.name) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-semibold text-gray-900 cursor-pointer hover:underline" @click="openUserPanel(req.user.id)">{{ req.user.name }}</div>
                <div class="text-sm text-gray-500 mt-0.5">{{ $t('community_curation_requested_to_join') }} &bull; {{ req.dateString }}</div>
                <div v-if="req.user.claims && req.user.claims.length" class="tw-flex items-center gap-1 mt-1.5 flex-wrap">
                  <span class="text-xs text-gray-400">{{ $t('community_curation_claims_label') }}</span>
                  <a
                    v-for="claim in req.user.claims"
                    :key="claim.elastic_id"
                    :href="'/actors/' + claim.elastic_id"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="actor-claim-chip"
                  >{{ claim.name }}</a>
                </div>
              </div>
              <div class="w-full sm:w-auto tw-flex items-center gap-2 flex-wrap justify-end">
                <template v-if="!req._processed">
                  <DsButton size="small" variant="rounded" :label="$t('knowledge_base_resource_actions_announcement_VIEW')" @click="openUserPanel(req.user.id)"/>
                  <DsButton size="small" variant="rounded" icon="check" :label="$t('community_curation_accept')" @click="acceptJoinRequest(req)"/>
                  <DsButton size="small" variant="rounded-secondary" :label="$t('community_curation_deny')" @click="denyJoinRequest(req)"/>
                </template>
                <span v-else class="text-sm font-medium" :class="req._decision === 'accept' ? 'text-green-600' : 'text-red-500'">
                  {{ req._decision === 'accept' ? $t('community_curation_accepted') : $t('community_curation_denied') }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ANNOUNCEMENTS -->
        <div v-else-if="activeTab === tabNames.announcements">
          <div v-if="announcements.length === 0" class="text-center py-16 px-8 bg-white rounded-lg border border-gray-200">
            <div class="bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4 text-gray-400" style="width:56px;height:56px;flex-shrink:0">
              <Icon name="bell" size="24"/>
            </div>
            <p class="text-base font-semibold text-gray-700 mb-1">{{ $t('community_curation_announcements_empty_title') }}</p>
            <p class="text-sm text-gray-400">{{ $t('community_curation_announcements_empty_sub') }}</p>
          </div>
          <div v-else class="tw-flex flex-col gap-3">
            <div
              v-for="ann in announcements"
              :key="ann.id"
              class="tw-flex flex-col gap-3 bg-white rounded-lg border border-gray-200 px-5 py-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div class="tw-flex flex-col sm:flex-row items-start sm:justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="font-semibold text-gray-900 truncate">{{ ann.title }}</div>
                  <div class="text-sm text-gray-500 mt-0.5">
                    {{ ann.user_name ?? ann.user?.name }}
                    &bull;
                    <span v-if="ann.communities && ann.communities.length">{{ ann.communities.map(c => c.label).join(', ') }}</span>
                    &bull; {{ formatDate(ann.created_at) }}
                  </div>
                </div>
                <div class="tw-flex items-center gap-2 flex-wrap shrink-0 justify-end">
                  <DsButton size="small" variant="rounded-minimal" :label="$t('preview')" @click="previewAnnouncement(ann)"/>
                  <DsButton size="small" variant="rounded" icon="check" :label="$t('community_curation_approve')" @click="approveAnnouncement(ann)"/>
                  <DsButton size="small" variant="rounded-secondary" icon="edit" :label="$t('edit')" @click="editAnnouncement(ann)"/>
                  <DsButton size="small" variant="rounded-secondary" icon="trash" :label="$t('community_curation_remove')" @click="removeAnnouncement(ann)"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- KNOWLEDGE BASE -->
        <div v-else-if="activeTab === tabNames.knowledgeBase">
          <div v-if="knowledgeBasePosts.length === 0" class="text-center py-16 px-8 bg-white rounded-lg border border-gray-200">
            <div class="bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4 text-gray-400" style="width:56px;height:56px;flex-shrink:0">
              <Icon name="book" size="18"/>
            </div>
            <p class="text-base font-semibold text-gray-700 mb-1">{{ $t('community_curation_kb_empty_title') }}</p>
            <p class="text-sm text-gray-400">{{ $t('community_curation_kb_empty_sub') }}</p>
          </div>
          <div v-else class="tw-flex flex-col gap-3">
            <div
              v-for="post in knowledgeBasePosts"
              :key="post.id"
              class="tw-flex flex-col gap-3 bg-white rounded-lg border border-gray-200 px-5 py-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div class="tw-flex flex-col sm:flex-row items-start sm:justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="font-semibold text-gray-900 truncate">{{ post.title }}</div>
                  <div class="text-sm text-gray-400 mt-0.5 line-clamp-2">{{ post.excerpt || (post.body ? post.body.substring(0, 120) + '…' : '') }}</div>
                  <div class="text-xs text-gray-500 mt-1">
                    <span @click.stop="openUserPanel(post.creator?.id)" class="hover:underline text-primary cursor-pointer">{{ post.creator?.name }}</span>
                    &bull; {{ formatDate(post.created_at) }}
                  </div>
                </div>
                <div class="tw-flex items-center gap-2 flex-wrap shrink-0 justify-end">
                  <DsButton size="small" variant="rounded-minimal" :label="$t('preview')" @click="previewKbPost(post)"/>
                  <DsButton size="small" variant="rounded" :label="$t('community_curation_approve')" :loading="post._approving" @click="approveKbPost(post)"/>
                  <DsButton size="small" variant="rounded-secondary" :label="$t('community_curation_reject')" :loading="post._rejecting" @click="rejectKbPost(post)"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- CHALLENGES -->
        <div v-else-if="activeTab === tabNames.challenges">
          <div v-if="challenges.length === 0" class="text-center py-16 px-8 bg-white rounded-lg border border-gray-200">
            <div class="bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4 text-gray-400" style="width:56px;height:56px;flex-shrink:0">
              <Icon name="comment" size="18"/>
            </div>
            <p class="text-base font-semibold text-gray-700 mb-1">{{ $t('community_curation_challenges_empty_title') }}</p>
            <p class="text-sm text-gray-400">{{ $t('community_curation_challenges_empty_sub') }}</p>
          </div>
          <div v-else class="tw-flex flex-col gap-3">
            <div
              v-for="ch in challenges"
              :key="ch.id"
              class="tw-flex flex-col gap-3 bg-white rounded-lg border border-gray-200 px-5 py-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div class="tw-flex flex-col sm:flex-row items-start sm:justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="font-semibold text-gray-900 truncate">{{ ch.title }}</div>
                  <div class="text-sm text-gray-500 mt-0.5">
                    <span class="text-primary cursor-pointer hover:underline" @click.stop="openUserPanel(ch.created_by)">{{ ch.user_name }}</span>
                    <span v-if="ch.communities && ch.communities.length"> &bull; {{ ch.communities.map(c => c.name).join(', ') }}</span>
                    &bull; {{ formatDate(ch.created_at) }}
                  </div>
                </div>
                <div class="tw-flex items-center gap-2 flex-wrap shrink-0 justify-end">
                  <DsButton size="small" variant="rounded-minimal" :label="$t('preview')"
                            @click="$router.push('/communities/'+this.communityId+'/challenges/' + ch.id + '?curation=challenge')"/>
                  <DsButton size="small" variant="rounded" icon="check" :label="$t('community_curation_approve')" @click="approveChallengeItem(ch)"/>
                  <DsButton size="small" variant="rounded-secondary" icon="edit" :label="$t('edit')" @click="editChallenge(ch)"/>
                  <DsButton size="small" variant="rounded-secondary" icon="trash" :label="$t('community_curation_remove')" @click="denyChallengeItem(ch)"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- NEW ACTORS (member only) -->
        <div v-else-if="activeTab === tabNames.newActors || activeTab === tabNames.claims || activeTab === tabNames.deletedActors">
          <div v-if="actors.length === 0" class="text-center py-16 px-8 bg-white rounded-lg border border-gray-200">
            <div class="bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4 text-gray-400" style="width:56px;height:56px;flex-shrink:0">
              <Icon name="building" size="18"/>
            </div>
            <p class="text-base font-semibold text-gray-700 mb-1">{{ $t('community_curation_actors_empty_title') }}</p>
          </div>
          <div v-else class="overflow-x-auto">
            <table class="table table--fill table--hover w-full" style="table-layout: fixed">
              <thead>
              <tr>
                <th>{{ $t('community_curation_col_name') }}</th>
                <th v-if="activeTab === tabNames.newActors || activeTab === tabNames.claims">{{ $t('community_curation_col_portfolio') }}</th>
                <th>{{ $t('community_curation_col_claim_status') }}</th>
                <th v-if="isOwner || isMember" colspan="3" style="width:300px;">{{ $t('community_curation_col_actions') }}</th>
              </tr>
              </thead>
              <tbody>
              <row-curation
                v-for="(actor, index) in actors"
                :key="index"
                :actor="actor"
                :is-deleted-tab="activeTab === tabNames.deletedActors"
                @ok="handleActorRemove(actor)"
              />
              </tbody>
            </table>
          </div>
        </div>

      </template>

      <!-- Pagination -->
      <Pagination
        v-if="paging && paging.total > paging.limit"
        class="simple-pagination--bottom mt-4"
        :paging="paging"
        @change="setOffset"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
  import Pagination from '@/components/Pagination/SimplePagination.vue'
  import RowCuration from '@/components/Datalab/RowCuration.vue'

  import { fetchCuration } from '@/api/curation.js'
  import { acceptPublicAnnouncement, denyPublicAnnouncement } from '@/api/notifications'
  import { acceptRequestToJoinCommunity, denyRequestToJoinCommunity } from '@/api/communities'
  import { approvePost, rejectPost } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import { acceptChallenge, denyChallenge } from '@/Domain/Challenge/Api/challenges.ts'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui.js'
  import TranslationsMixin from '@/util/TranslationsMixin.js'
  import UserMixin from '@/util/UserMixin'
  import CommunityMixin from '@/util/CommunityMixin'
  import MODAL_IDS from '@/constants/modal-ids'

  export default defineComponent({
    name: 'CommunityCuration',
    components: { DsInput, DsButton, Icon, Loading, Pagination, RowCuration },
    mixins: [TranslationsMixin, UserMixin, CommunityMixin],
    data() {
      return {
        community: {},
        loading: false,
        actors: [],
        announcements: [],
        challenges: [],
        communityJoinRequests: [],
        knowledgeBasePosts: [],
        paging: null,
        curationCount: null,
        searchQuery: '',
        activeTab: 'join_requests',
        tabNames: {
          communityJoinRequests: 'join_requests',
          announcements: 'announcements',
          knowledgeBase: 'knowledge_base',
          challenges: 'challenges',
          newActors: 'new_actors',
          claims: 'claims',
        },
      }
    },
    computed: {
      communityId(): number {
        return Number(this.$route.params.communityId)
      },
      knowledgePanelEnabled(): boolean {
        return (this.$store.state.config.viewDashboard || []).includes('knowledge-base')
      },
      challengesEnabled(): boolean {
        return this.$store.state.config.challengesAreEnabled
      },
      tabs(): string[] {
        const t = [this.tabNames.communityJoinRequests, this.tabNames.announcements]
        if (this.knowledgePanelEnabled) t.push(this.tabNames.knowledgeBase)
        if (this.challengesEnabled) t.push(this.tabNames.challenges)
        return t
      },
    },
    methods: {
      getDisabledTabTooltip(tab: string) {
        switch (tab) {
          case this.tabNames.challenges:
            return this.$t('communities_curation_challenges_disabled', { challenges: this.challengesLabel })
          case this.tabNames.announcements:
            return this.$t('communities_curation_announcements_disabled', { announcements: this.announcementsLabel })
          case this.tabNames.knowledgeBase:
            return this.$t('communities_curation_knowledgeBase_disabled', { knowledge_base: this.knowledgeBaseLabel })
          default:
            return true
        }
      },
      tabIsEnabled(tab: string): boolean {
        switch (tab) {
          case this.tabNames.challenges:
            return this.community.enableChallenges
          case this.tabNames.announcements:
            return this.community.enableAnnouncementsAndEvents
          case this.tabNames.knowledgeBase:
            return this.community.enableKnowledgeBase
          default:
            return true
        }
      },
      getTabCount(tab: string): number {
        if (!this.curationCount) return 0
        const map: Record<string, number> = {
          [this.tabNames.communityJoinRequests]: this.curationCount.communityJoinRequests,
          [this.tabNames.announcements]: this.curationCount.announcements,
          [this.tabNames.knowledgeBase]: this.curationCount.knowledgeBase,
          [this.tabNames.challenges]: this.curationCount.challenges,
          [this.tabNames.newActors]: this.curationCount.draft,
          [this.tabNames.claims]: this.curationCount.claim,
          [this.tabNames.deletedActors]: this.curationCount.deleted,
        }
        return map[tab] || 0
      },
      switchTab(tab: string) {
        this.activeTab = tab
        this.actors = []
        this.paging = null
        this.fetch()
      },
      formatDate(str: string): string {
        if (!str) return ''
        return new Date(str).toLocaleDateString()
      },
      initials(name: string): string {
        if (!name) return '?'
        return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
      },
      openUserPanel(userId: number) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'user-information-side-panel',
          metaData: { userId, allowDelete: false },
        })
      },

      // ── JOIN REQUESTS ─────────────────────────────────────────
      acceptJoinRequest(req: any) {
        acceptRequestToJoinCommunity(req.communityId, req.id).then(() => {
          req._processed = true
          req._decision = 'accept'
          if (this.curationCount) this.curationCount.communityJoinRequests = Math.max(0, (this.curationCount.communityJoinRequests || 1) - 1)
        })
      },
      denyJoinRequest(req: any) {
        denyRequestToJoinCommunity(req.communityId, req.id).then(() => {
          req._processed = true
          req._decision = 'deny'
          if (this.curationCount) this.curationCount.communityJoinRequests = Math.max(0, (this.curationCount.communityJoinRequests || 1) - 1)
        })
      },

      // ── ANNOUNCEMENTS ─────────────────────────────────────────
      previewAnnouncement(ann: any) {
        if (ann.communities && ann.communities.length > 0) {
          this.$router.push('/communities/' + ann.communities[0].value + '/announcements/' + (ann.slug || ann.id) + '?curation=announcement')
          return
        }
        this.$router.push('/announcements/' + (ann.slug || ann.id) + '?curation=announcement')
      },
      approveAnnouncement(ann: any) {
        acceptPublicAnnouncement(ann.id).then(() => {
          this.announcements = this.announcements.filter((a: any) => a.id !== ann.id)
          this.$bus.emit('acceptedAnnouncement')
          if (this.curationCount) this.curationCount.announcements = Math.max(0, (this.curationCount.announcements || 1) - 1)
        })
      },
      removeAnnouncement(ann: any) {
        denyPublicAnnouncement(ann.id).then(() => {
          this.announcements = this.announcements.filter((a: any) => a.id !== ann.id)
          this.$bus.emit('deniedAnnouncement')
          if (this.curationCount) this.curationCount.announcements = Math.max(0, (this.curationCount.announcements || 1) - 1)
        })
      },
      editAnnouncement(announcement: any) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { announcement })
        if (announcement.type === 'event') {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.MANAGE_EVENT)
        } else {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.MANAGE_ANNOUNCEMENT)
        }
      },

      // ── KNOWLEDGE BASE ────────────────────────────────────────
      previewKbPost(post: any) {
        this.$router.push(`/communities/${this.communityId}/knowledge-base/posts/${post.id}?curation=knowledge_base`)
      },
      async approveKbPost(post: any) {
        post._approving = true
        try {
          await approvePost(post.id, post.community_id)
          this.knowledgeBasePosts = this.knowledgeBasePosts.filter((p: any) => p.id !== post.id)
          if (this.curationCount) this.curationCount.knowledgeBase = Math.max(0, (this.curationCount.knowledgeBase || 1) - 1)
        } finally {
          post._approving = false
        }
      },
      async rejectKbPost(post: any) {
        post._rejecting = true
        try {
          await rejectPost(post.id, post.community_id)
          this.knowledgeBasePosts = this.knowledgeBasePosts.filter((p: any) => p.id !== post.id)
          if (this.curationCount) this.curationCount.knowledgeBase = Math.max(0, (this.curationCount.knowledgeBase || 1) - 1)
        } finally {
          post._rejecting = false
        }
      },

      // ── CHALLENGES ────────────────────────────────────────────
      approveChallengeItem(ch: any) {
        acceptChallenge(ch.id).then(() => {
          this.challenges = this.challenges.filter((c: any) => c.id !== ch.id)
          this.$bus.emit('acceptedChallenge')
          if (this.curationCount) this.curationCount.challenges = Math.max(0, (this.curationCount.challenges || 1) - 1)
        })
      },
      denyChallengeItem(ch: any) {
        denyChallenge(ch.id).then(() => {
          this.challenges = this.challenges.filter((c: any) => c.id !== ch.id)
          this.$bus.emit('deniedChallenge')
          if (this.curationCount) this.curationCount.challenges = Math.max(0, (this.curationCount.challenges || 1) - 1)
        })
      },
      editChallenge(ch: any) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'manage-challenge-panel',
          metaData: ch,
        })
      },

      // ── ACTORS ────────────────────────────────────────────────
      handleActorRemove(actor: any) {
        this.actors.splice(this.actors.indexOf(actor), 1)
        if (this.actors.length === 0) this.fetch()
      },

      setOffset(offset: number) {
        this.fetch(offset)
      },

      fetchCounts() {
        fetchCuration({ type: 'count-all', communityId: this.communityId })
          .then(({ data }: any) => {
            this.curationCount = data
          })
      },

      fetch(offset = 0) {
        this.loading = true
        this.fetchCounts()

        const base = { communityId: this.communityId, offset, limit: 20 }

        const typeMap: Record<string, string> = {
          [this.tabNames.communityJoinRequests]: 'communityJoinRequests',
          [this.tabNames.announcements]: 'announcements',
          [this.tabNames.knowledgeBase]: 'knowledgeBase',
          [this.tabNames.challenges]: 'challenges',
          [this.tabNames.newActors]: 'draft',
          [this.tabNames.claims]: 'claim',
          [this.tabNames.deletedActors]: 'deleted',
        }

        const type = typeMap[this.activeTab] || 'announcements'
        const filters = { ...base, type, ...(this.searchQuery ? { name: this.searchQuery } : {}) }

        fetchCuration(filters)
          .then(({ data, paging }: any) => {
            this.paging = paging
            if (type === 'communityJoinRequests') this.communityJoinRequests = data
            else if (type === 'announcements') this.announcements = data
            else if (type === 'knowledgeBase') this.knowledgeBasePosts = data
            else if (type === 'challenges') this.challenges = data
            else this.actors = data
          })
          .finally(() => {
            this.loading = false
          })
      },
    },
    mounted() {
      this.community = this.getCommunity()
      if (this.$route.query.curation && this.$route.query.curation === 'announcement') {
        this.activeTab = this.tabs[1]
      } else if (this.$route.query.curation && this.$route.query.curation === 'challenge') {
        this.activeTab = this.tabs[3]
      } else if (this.$route.query.curation && this.$route.query.curation === 'knowledge_base') {
        this.activeTab = this.tabs[2]
      } else if (this.tabs.length > 0) this.activeTab = this.tabs[0]

      this.fetch()
    },
    created() {
      this.$bus.on('editAndAcceptedAnnouncement', () => this.fetch())
      this.$bus.on('acceptedAnnouncement', () => this.fetch())
      this.$bus.on('deniedAnnouncement', () => this.fetch())
      this.$bus.on('challengeUpdated', () => this.fetch())
      this.$bus.on('acceptedChallenge', () => this.fetch())
      this.$bus.on('deniedChallenge', () => this.fetch())
    },
    beforeUnmount() {
      this.$bus.off('editAndAcceptedAnnouncement')
      this.$bus.off('acceptedAnnouncement')
      this.$bus.off('deniedAnnouncement')
      this.$bus.off('challengeUpdated')
      this.$bus.off('acceptedChallenge')
      this.$bus.off('deniedChallenge')
    },
    watch: {
      searchQuery() {
        this.fetch()
      },
    },
  })
</script>

<style lang="scss" scoped>
  .tab-pill {
    cursor: pointer;
    border: none;
    background: transparent;

    &--active {
      background-color: var(--primary-community, #3b82f6);
      color: white;
    }
  }

  .badge-count--active {
    background: rgba(255, 255, 255, 0.25);
    color: white;
  }

  .actor-claim-chip {
    display: inline-flex;
    align-items: center;
    color: var(--primary-community-light);
    font-size: 0.75rem;
    font-weight: 500;
    text-decoration: underline;
    transition: color 0.15s;

    &:hover {
      color: var(--primary-community);
    }
  }
</style>
